<template>
  <div>
    <LfBlueTitleComponent :title="texts.title" />
    <div class="step-wizard-container">
      <h2 class="step-title"> {{ texts.caption }}</h2>
      <div class="step-instructions" > {{ texts.body }} </div>
      <TemplateSelectorTreeComponent
        :idEntityType="model.templateEntityTypeId"
        class="invoices-template-selector-container"
        @templateSelected="onTemplateSelected"
      />
      <div class="dialog-actions-container" >
        <LfActionsBarComponent
          :buttons="buttons"
          @execute-action="onExecuteAction"
          class="action-buttons"
          :showKebabMenu="false"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import TemplateSelectorTreeComponent
  from '@/components/template/TemplateSelectorComponent/TemplateSelectorTreeComponent.vue'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import { computed, PropType } from 'vue'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import LfBlueTitleComponent from '@/components/HeaderTitle/LfBlueTitleComponent.vue'
import { ITemplate } from '@/store/modules/template/templateTypes'
import { IFacturaEDialogModel } from '@/components/billing/FacturaEDialog/types/FacturaEDialogTypes'
import useActionsFacturaEDialog from '@/components/billing/FacturaEDialog/composables/useActionsFacturaEDialog'
import { useI18n } from 'vue-i18n-composable'

const props = defineProps({
  model: {
    type: Object as PropType<IFacturaEDialogModel>,
    required: true
  }
})
const { t } = useI18n()
const texts = {
  title: t('components.facturae_dialog.template_selector.title').toString(),
  caption: t('components.facturae_dialog.template_selector.caption').toString(),
  body: t('components.facturae_dialog.template_selector.body').toString(),
  cancel: t('action_buttons.cancel').toString(),
  generate: t('action_buttons.generate').toString(),
}
const buttons = computed(() => {
  return [
    {
      tooltip: texts.cancel,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.CLOSE,
      enabled: true
    },
    {
      tooltip: texts.generate,
      variant: LabelButtonVariant.SOLID,
      action: ActionName.NEXT,
      enabled: !!props.model.selectedTemplate
    },
  ]
})

const { next, close, send } = useActionsFacturaEDialog(props.model)

function onTemplateSelected(templateSelected: ITemplate) {
  props.model.selectedTemplate = templateSelected
}

function onExecuteAction(action: string ) {
  switch (action) {
    case ActionName.NEXT:
      next()
      return
    case ActionName.CLOSE:
      close()
      return
    case ActionName.SEND_FACTURA_E:
      send()
      return
  }
}
</script>

<style scoped lang="scss">
.invoices-template-selector-container {
  ::v-deep .no-result-dialog {
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    height: 343px;
    max-height: 100%;
  }
}
.invoices-template-selector-container {
  margin-top: 0;
  padding-top: 0;

  ::v-deep .v-treeview {
    .v-icon {
      &::before {
        font-size: 18px;
        color: $corporate-color;
      }

      &::after {
        display: none;
      }
    }

    .is-directory,
    .is-template {
      font-size: 14px;
    }

    .is-directory {
      color: $corporate-color;
    }

    .is-template {
      color: $black-01;
    }
  }
}
.step-wizard-container {
  width: 100%;

  .step-title {
    @include milano-bold-18;
    color: $main-1000;
  }

  .step-instructions {
    @include milano-regular-14;
    color: $neutral-grey-800;
    margin-top: $spacing-xs;
  }

  .static-alert {
    margin-top: $spacing-md;
  }
}
.dialog-actions-container {
  @include flex($justify-content: flex-end);
  width: 100%;
  margin-top: $spacing-xl;

  ::v-deep .main-action-button {
    @include main-action-button--rectangle;
  }

  ::v-deep .secondary-action-button {
    @include secondary-action-button--rectangle;
  }
}
</style>
